import {
  useGetOffersCargos,
  useMultipleGetOffersCargos,
} from "@/entities/get-offers-cargos/fetch";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { excludeNull } from "@/shared/lib/collection";
import type { LoadableData } from "@/shared/lib/loadable";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import type { CarrierCargoListPageProps } from "../model";

export const useCarrierCargoListPageProps = (params: {
  tab: "not_proposed" | "proposed" | undefined;
}): CarrierCargoListPageProps => {
  const navigate = useNavigate();

  // Auth
  const auth = useAuth();

  // State
  const [getDraftOffersCargosParams, setGetDraftOffersCargosParams] =
    useState<
      Parameters<
        CarrierCargoListPageProps["onGetDraftOffersCargosParamsChange"]
      >[0]
    >();
  const [offerCargoPagination, setOfferCargoPagination] = useState<{
    page: number;
    perPage: number;
  }>({
    page: 1,
    perPage: 50,
  });

  // Backend API
  const getDraftOffersCargos = useGetOffersCargos({
    ...getDraftOffersCargosParams,
    stage: ["draft"],
    pagination: {
      // pageNumber: offerCargoPagination.page,
      // displayLimit: offerCargoPagination.perPage,
      // WORKAROUND: FREIGHT_MATCHING-320 【荷主/車主】新規荷物を登録した際、新しく登録した荷物が2ページ目の頭に表示されてしまう
      // WORKAROUND: こちらのバグがデモに支障あるため、一旦workaroundを入れています。正しくはAPIのI/Fにソート条件を含めるべきです。
      displayLimit: 10000,
    },
  });
  // Sort cargos by ID in ascending order and create params object
  const getOffersCargosPerProcess = useMultipleGetOffersCargos(
    Object.fromEntries(
      (getDraftOffersCargos.data?.data.cargos ?? []).map((cargo) => [
        cargo.process.id,
        {
          processId: cargo.process.id,
        },
      ]),
    ),
    {
      enabled: getDraftOffersCargos.status === "success",
      sortFunc: (a, b) => {
        if (!a.processId || !b.processId) {
          return 0;
        }
        // 降順でソート
        return b.processId - a.processId;
      },
    },
  );

  // Actions
  const onChangeTab: CarrierCargoListPageProps["onChangeTab"] = (tab) => {
    navigate({
      to: "/carrier/cargos",
      search: {
        tab,
      },
    });
  };
  const onGetDraftOffersCargosParamsChange: CarrierCargoListPageProps["onGetDraftOffersCargosParamsChange"] =
    (filter) => {
      setGetDraftOffersCargosParams(filter);
    };
  const onGetDraftOffersCargosParamsReset: CarrierCargoListPageProps["onGetDraftOffersCargosParamsReset"] =
    () => {
      setGetDraftOffersCargosParams(undefined);
    };

  // Props
  const user: CarrierCargoListPageProps["user"] = useMemo(() => {
    if (!auth.session.isAuthenticated) {
      return {
        status: "loading",
        data: undefined,
      };
    }
    return {
      status: "success",
      data: auth.session.user,
    };
  }, [auth.session.isAuthenticated, auth.session]);
  const draftOfferCargos = useMemo<
    CarrierCargoListPageProps["draftOfferCargos"]
  >(() => {
    if (getDraftOffersCargos.status === "pending") {
      return {
        status: "loading",
        data: undefined,
      };
    }
    if (getDraftOffersCargos.status === "error") {
      return {
        status: "error",
        error: { message: JSON.stringify(getDraftOffersCargos.error) },
      };
    }
    const data =
      getDraftOffersCargos.data.data.cargos?.map((cargo) => {
        if (!auth.session.isAuthenticated) {
          return null;
        }
        if (cargo.company?.id === auth.session.user.companyId) {
          // 自社の荷物は表示しない
          // TODO: API側で対応するべきなので、暫定的なworkaroundです
          return null;
        }
        const process: LoadableData<
          CarrierCargoListPageProps["draftOfferCargos"]
        >["data"][0]["process"] = (() => {
          const offerCargosInProcess =
            getOffersCargosPerProcess[cargo.process.id];
          if (
            !offerCargosInProcess ||
            offerCargosInProcess.status === "pending"
          ) {
            return {
              status: "loading",
              data: undefined,
            };
          }
          if (offerCargosInProcess.status === "error") {
            return {
              status: "error",
              error: { message: JSON.stringify(offerCargosInProcess.error) },
            };
          }
          const deliveryProposalOfferCargos =
            offerCargosInProcess.data.data.cargos?.filter(
              (offerCargo) => offerCargo.stage === "delivery_proposal",
            ) ?? [];
          const contractProposalOfferCargo =
            offerCargosInProcess.data.data.cargos?.find(
              (offerCargo) => offerCargo.stage === "contract_proposal",
            ) ?? undefined;
          const contractConcludedOfferCargo =
            offerCargosInProcess.data.data.cargos?.find(
              (offerCargo) => offerCargo.stage === "contract_concluded",
            ) ?? undefined;
          return {
            status: "success",
            data: {
              ...cargo.process,
              deliveryProposalOfferCargos,
              contractProposalOfferCargo,
              contractConcludedOfferCargo,
            },
          };
        })();
        return {
          ...cargo,
          process,
        };
      }) ?? [];
    return {
      status: "success",
      data: {
        data: excludeNull(data),
        pagination: {
          ...offerCargoPagination,
          totalCount: getDraftOffersCargos.data.data.totalCount ?? 0,
          onPageChange: (page: number) =>
            setOfferCargoPagination((prev) => ({ ...prev, page })),
          onPerPageChange: (perPage: number) =>
            setOfferCargoPagination((prev) => ({ ...prev, perPage })),
        },
      },
    };
  }, [
    offerCargoPagination,
    getDraftOffersCargos.status,
    getDraftOffersCargos.error,
    getDraftOffersCargos.data,
    getOffersCargosPerProcess,
    auth.session,
  ]);
  return {
    selectedTab: params.tab ?? "not_proposed",
    user,
    draftOfferCargos,
    getDraftOffersCargosParams,
    onChangeTab,
    onGetDraftOffersCargosParamsChange,
    onGetDraftOffersCargosParamsReset,
  };
};
